import type { Ride } from './config.js';

/**
 * Trick vocabulary per ride. The wire `k` field encodes `kind << 3 | variant`, so remote
 * clients can play the same animation without any scoring data crossing the socket.
 */
export const TRICK_KIND = { none: 0, flip: 1, grab: 2, grind: 3 } as const;
export type TrickKind = (typeof TRICK_KIND)[keyof typeof TRICK_KIND];

export interface RideTricks {
  flips: readonly string[];
  grabs: readonly string[];
  grinds: readonly string[];
}

export const TRICKS: Record<Ride, RideTricks> = {
  skateboard: {
    flips: ['Kickflip', 'Heelflip', 'Tre Flip', 'Hardflip'],
    grabs: ['Indy', 'Melon', 'Method', 'Stalefish'],
    grinds: ['50-50', 'Boardslide', 'Crooked', 'Nosegrind'],
  },
  bmx: {
    flips: ['Tailwhip', 'Barspin', 'Double Whip', 'Bar Tailwhip'],
    grabs: ['No-Hander', 'Can-Can', 'Superman', 'Tabletop'],
    grinds: ['Double Peg', 'Feeble', 'Smith', 'Icepick'],
  },
  inline: {
    flips: ['Flatspin', 'Misty', 'Cork', 'Backflip'],
    grabs: ['Mute', 'Japan', 'Stale', 'Safety'],
    grinds: ['Soul', 'Royale', 'Makio', 'Backslide'],
  },
};

export function encodeTrick(kind: TrickKind, variant: number): number {
  return ((kind & 3) << 3) | (variant & 7);
}

export function decodeTrick(code: number): { kind: TrickKind; variant: number } {
  return { kind: ((code >> 3) & 3) as TrickKind, variant: code & 7 };
}

export function trickName(ride: Ride, code: number): string | null {
  const { kind, variant } = decodeTrick(code);
  const list =
    kind === TRICK_KIND.flip
      ? TRICKS[ride].flips
      : kind === TRICK_KIND.grab
        ? TRICKS[ride].grabs
        : kind === TRICK_KIND.grind
          ? TRICKS[ride].grinds
          : null;
  return list?.[variant % list.length] ?? null;
}
